import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    CircularProgress,
    alpha
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Block as BlockIcon,
    ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { APIKeyOut, APIKeyRegenerateResponse } from 'src/api/Api';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { formatDate } from 'src/util/date-utils';

interface ApiKeysProps {
    refreshTrigger?: number;
}

const ApiKeys: React.FC<ApiKeysProps> = ({ refreshTrigger }) => {
    const [apiKeys, setApiKeys] = useState<APIKeyOut[]>([]);
    const [loading, setLoading] = useState(false);
    const { showSnackbar, snackbarType } = useSnackbar();

    // Dialog states
    const [regenerateDialog, setRegenerateDialog] = useState<{ open: boolean, apiKeyId: string | null }>({ open: false, apiKeyId: null });
    const [newKeyData, setNewKeyData] = useState<APIKeyRegenerateResponse | null>(null);
    const [revokeDialog, setRevokeDialog] = useState<{ open: boolean, apiKeyId: string | null }>({ open: false, apiKeyId: null });
    const [actionLoading, setActionLoading] = useState(false);

    const fetchApiKeys = async () => {
        setLoading(true);
        try {
            // @ts-ignore - The generated client might not have updated types yet or strict mode issue
            const response = await clientService.api.listApiKeysApiV1ApiKeysGet();
            if (response.ok) {
                // Handle both array (old) and object (new) response formats for safety
                const data = response.data as any;
                if (Array.isArray(data)) {
                    setApiKeys(data);
                } else if (data && Array.isArray(data.api_keys)) {
                    setApiKeys(data.api_keys);
                } else {
                    console.error('Unexpected API response format:', data);
                    setApiKeys([]);
                }
            }
        } catch (error) {
            console.error('Failed to fetch API keys:', error);
            showSnackbar({ message: 'Failed to fetch API keys', type: snackbarType.ERROR });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchApiKeys();
    }, [refreshTrigger]);

    const handleRevoke = async () => {
        if (!revokeDialog.apiKeyId) return;
        setActionLoading(true);
        try {
            const response = await clientService.api.revokeApiKeyApiV1ApiKeysApiKeyIdRevokePut(revokeDialog.apiKeyId);
            if (response.ok) {
                showSnackbar({ message: 'API Key revoked successfully', type: snackbarType.SUCCESS });
                fetchApiKeys();
                setRevokeDialog({ open: false, apiKeyId: null });
            }
        } catch (error) {
            console.error('Failed to revoke API key:', error);
            showSnackbar({ message: 'Failed to revoke API key', type: snackbarType.ERROR });
        } finally {
            setActionLoading(false);
        }
    };

    const handleRegenerate = async () => {
        if (!regenerateDialog.apiKeyId) return;
        setActionLoading(true);
        try {
            const response = await clientService.api.regenerateApiKeyApiV1ApiKeysApiKeyIdRegeneratePost(regenerateDialog.apiKeyId);
            if (response.ok) {
                setNewKeyData(response.data);
                showSnackbar({ message: 'API Key regenerated successfully', type: snackbarType.SUCCESS });
                fetchApiKeys();
            }
        } catch (error) {
            console.error('Failed to regenerate API key:', error);
            showSnackbar({ message: 'Failed to regenerate API key', type: snackbarType.ERROR });
            setRegenerateDialog({ open: false, apiKeyId: null });
        } finally {
            setActionLoading(false);
        }
    };

    const handleCloseRegenerateDialog = () => {
        setRegenerateDialog({ open: false, apiKeyId: null });
        setNewKeyData(null);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        showSnackbar({ message: 'Copied to clipboard', type: snackbarType.INFO });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>


            <Box sx={{
                bgcolor: 'rgba(28, 28, 28, 0.4)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                overflow: 'hidden'
            }}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr 1.5fr 2fr 1fr 1.2fr 100px',
                    py: 1.5,
                    px: 3,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    bgcolor: 'rgba(255, 255, 255, 0.02)'
                }}>
                    {['KEY PREFIX', 'USER NAME', 'USER EMAIL', 'STATUS', 'CREATED AT', 'ACTIONS'].map((h, i) => (
                        <Typography key={h} variant="caption" sx={{ color: i === 0 ? '#10B981' : 'rgba(255, 255, 255, 0.3)', fontWeight: 700, letterSpacing: '1px' }}>
                            {h}
                        </Typography>
                    ))}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: '#10B981' }} />
                    </Box>
                ) : apiKeys.length === 0 ? (
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', textAlign: 'center', py: 8 }}>
                        No API keys found
                    </Typography>
                ) : (
                    apiKeys.map((key) => (
                        <Box key={key.id} sx={{
                            display: 'grid',
                            gridTemplateColumns: '1.5fr 1.5fr 2fr 1fr 1.2fr 100px',
                            alignItems: 'center',
                            py: 2,
                            px: 3,
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.02)' }
                        }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontFamily: 'monospace', bgcolor: 'rgba(255,255,255,0.05)', px: 1, py: 0.5, borderRadius: 1, width: 'fit-content' }}>
                                {key.key_prefix}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#fff' }}>{(key as any).user_name || '-'}</Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>{(key as any).user_email || '-'}</Typography>
                            <Box>
                                <Chip 
                                    label={key.status} 
                                    size="small"
                                    sx={{
                                        bgcolor: alpha(key.status === 'active' ? '#00E676' : '#9e9e9e', 0.1),
                                        color: key.status === 'active' ? '#00E676' : '#9e9e9e',
                                        fontWeight: 700,
                                        fontSize: '10px',
                                        borderRadius: '4px',
                                        textTransform: 'uppercase'
                                    }}
                                />
                            </Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>{formatDate(key.created_at)}</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Regenerate Key">
                                    <IconButton 
                                        size="small"
                                        onClick={() => setRegenerateDialog({ open: true, apiKeyId: key.id })}
                                        sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#10B981', bgcolor: alpha('#10B981', 0.1) } }}
                                        disabled={key.status !== 'active'}
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Revoke Key">
                                    <IconButton 
                                        size="small"
                                        onClick={() => setRevokeDialog({ open: true, apiKeyId: key.id })}
                                        sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#FF5252', bgcolor: alpha('#FF5252', 0.1) } }}
                                        disabled={key.status !== 'active'}
                                    >
                                        <BlockIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ))
                )}
            </Box>

            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)', mt: 1 }}>
                Showing 1 to {apiKeys.length} of {apiKeys.length} API keys
            </Typography>

            {/* Regenerate Dialog - Styled to match theme */}
            <Dialog 
                open={regenerateDialog.open} 
                onClose={newKeyData ? undefined : handleCloseRegenerateDialog} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: '#121212',
                        color: '#fff',
                        borderRadius: '16px',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Regenerate API Key</DialogTitle>
                <DialogContent>
                    {newKeyData ? (
                        <Box sx={{ mt: 2 }}>
                            <Alert severity="success" sx={{ mb: 3, bgcolor: alpha('#00E676', 0.1), color: '#00E676', border: '1px solid rgba(0, 230, 118, 0.2)' }}>
                                {newKeyData.message}
                            </Alert>
                            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1 }}>
                                New API Key (Copy this now, it won't be shown again):
                            </Typography>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                bgcolor: 'rgba(255,255,255,0.05)', 
                                p: 2, 
                                borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,0.1)',
                                wordBreak: 'break-all'
                            }}>
                                <Typography variant="body2" sx={{ flexGrow: 1, fontFamily: 'monospace', color: '#10B981' }}>
                                    {newKeyData.api_key}
                                </Typography>
                                <IconButton onClick={() => copyToClipboard(newKeyData.api_key)} size="small" sx={{ color: '#10B981' }}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    ) : (
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', py: 1 }}>
                            Are you sure you want to regenerate this API key? The old key will be immediately revoked and deleted. This action cannot be undone.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    {newKeyData ? (
                        <Button 
                            onClick={handleCloseRegenerateDialog} 
                            variant="contained"
                            sx={{ bgcolor: '#10B981', color: '#fff', borderRadius: '8px', px: 4 }}
                        >
                            Close
                        </Button>
                    ) : (
                        <>
                            <Button onClick={handleCloseRegenerateDialog} sx={{ color: 'rgba(255,255,255,0.5)' }}>Cancel</Button>
                            <Button 
                                onClick={handleRegenerate} 
                                variant="contained" 
                                disabled={actionLoading}
                                sx={{ bgcolor: '#10B981', color: '#fff', borderRadius: '8px', px: 4, '&:hover': { bgcolor: '#059669' } }}
                            >
                                {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Regenerate'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Revoke Dialog - Styled */}
            <Dialog 
                open={revokeDialog.open} 
                onClose={() => setRevokeDialog({ open: false, apiKeyId: null })}
                PaperProps={{
                    sx: {
                        bgcolor: '#121212',
                        color: '#fff',
                        borderRadius: '16px',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Revoke API Key</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', py: 1 }}>
                        Are you sure you want to revoke this API key? Applications using this key will no longer be able to access the API.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setRevokeDialog({ open: false, apiKeyId: null })} sx={{ color: 'rgba(255,255,255,0.5)' }}>Cancel</Button>
                    <Button 
                        onClick={handleRevoke} 
                        variant="contained" 
                        disabled={actionLoading}
                        sx={{ bgcolor: '#FF5252', color: '#fff', borderRadius: '8px', px: 4, '&:hover': { bgcolor: '#D32F2F' } }}
                    >
                        {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Revoke'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};


export default ApiKeys;
